import React from 'react';

import { DEFAULT_FILTERS } from '@qality/common-config/src/defaultFilters';
import {
  IssueService,
  SettingsService,
  StorageOfUniqueServices,
} from '@qality/common-service';

import { AddTestCasesContext } from './AddTestCasesContext';
import { getProjectsFromIssues } from './AddTestCasesProvider.util';

type Props = {
  children: JSX.Element;
};

export const AddTestCasesProvider = ({ children }: Props): JSX.Element => {
  const [returnURL, setReturnURL] = React.useState(null);
  const [availableIssues, setAvailableIssues] = React.useState(null);
  const [availableProjects, setAvailableProjects] = React.useState([]);
  const [isAddingTestCases, setIsAddingTestCases] = React.useState(false);
  const [addingTestCasesLength, setAddingTestCasesLength] = React.useState(0);

  const addTestCasesToTestCycle = React.useCallback(
    (selectedIssues, testCycleId) => {
      setIsAddingTestCases(true);
      setAddingTestCasesLength(selectedIssues.length);

      return StorageOfUniqueServices.get('TestCyclesServiceUnique')
        .addTestCases(selectedIssues, testCycleId)
        .then((response) => {
          if (response.status === 200) {
            return response;
          }

          setIsAddingTestCases(false);
          return;
        });
    },
    []
  );

  const doSearchRequest = React.useCallback(async (query) => {
    try {
      const issues = await IssueService.getAllIssuesByJqlFilteredByQAlityType({
        jql: `${query}`,
        fields: ['issuetype', 'project', 'summary'],
      });

      const availableProjectsFromJQL =
        await SettingsService.filterProjectsByAvailability(
          getProjectsFromIssues(issues)
        );

      setAvailableIssues(issues);
      setAvailableProjects(availableProjectsFromJQL);
    } catch (e) {
      setAvailableIssues([]);
    }
  }, []);

  React.useEffect(() => {
    const initSearch = async () => {
      const urlSearchParams = await StorageOfUniqueServices.get(
        'URLService'
      ).getJiraURLSearchParams();

      const jqlQueryParam = urlSearchParams.get('jql');
      const filterQueryParam = urlSearchParams.get('filterId');
      let jqlValue;

      const issuesUrl = StorageOfUniqueServices.get('URLService').getIssuesUrl({
        jql: jqlQueryParam,
        filter: filterQueryParam,
      });
      if (jqlQueryParam) {
        jqlValue = jqlQueryParam;
      } else if (DEFAULT_FILTERS[filterQueryParam]) {
        jqlValue = DEFAULT_FILTERS[filterQueryParam];
      } else if (filterQueryParam) {
        const filterResponse = await IssueService.getFilter(filterQueryParam);
        const { jql: jqlFromFilters } = filterResponse;
        if (jqlFromFilters) {
          jqlValue = decodeURIComponent(jqlFromFilters);
        }
      } else {
        jqlValue = DEFAULT_FILTERS.default;
      }

      doSearchRequest(jqlValue);

      setReturnURL(issuesUrl);
    };
    initSearch();
  }, []);

  return (
    <AddTestCasesContext.Provider
      value={{
        returnURL,
        availableProjects,
        availableIssues: availableIssues ? availableIssues : [],
        addTestCasesToTestCycle,
        isAddingTestCases,
        addingTestCasesLength,
        isFetchingIssues: availableIssues === null,
      }}
    >
      {children}
    </AddTestCasesContext.Provider>
  );
};
